// ─── BiodiversityMap · mapa de áreas protegidas y biodiversidad ───────────
// Mapa interactivo Leaflet con capas WMS oficiales que cruzan el emplazamiento
// con las figuras de protección ambiental relevantes para:
//   · ESRS E4-5 "sites in or near protected areas"
//   · EU Taxonomy Obj 6 DNSH biodiversidad
//   · TNFD LEAP paso "Locate"
//   · BREEAM USO 02/03/05
//   · GRI 304
//
// Fuentes WMS:
//   · MITECO (nacional)              → Natura 2000, Red Parques, Reservas Biosfera,
//                                       Hábitats Anexo I, Humedales (IEZH),
//                                       Montes Utilidad Pública
//   · EEA · European Environment Agency → Natura 2000 EU (fallback internacional)
//   · IDECAN (Canarias)              → Red Canaria de Espacios Naturales Protegidos
//
// NOTA: algunos endpoints MITECO han cambiado de dominio (mapama.gob.es →
// miteco.gob.es) y pueden requerir ajuste en producción. Los endpoints
// marcados como "verificar en prod" son los más susceptibles. Si una capa
// no carga, el usuario puede desactivarla con el toggle · el mapa funciona.

import { useEffect, useRef, useState } from 'react'
import SplitLegend from './SplitLegend'
import { supabase } from '../../lib/supabase'

// ─── Endpoints WMS ────────────────────────────────────────────────────────
// NOTA: las URLs de MITECO en `wms.mapama.gob.es` dejaron de responder (dominio
// migró a miteco.gob.es sin equivalencia 1:1 directa). Capas MITECO desactivadas
// temporalmente hasta identificar los nuevos endpoints oficiales.
// Para España usamos las capas de EEA (European Environment Agency) que son
// equivalentes y abiertas con uso comercial permitido con atribución.
const WMS_EEA_NATURA     = 'https://bio.discomap.eea.europa.eu/arcgis/services/ProtectedSites/Natura2000Sites/MapServer/WMSServer'

// ─── Catálogo de capas ────────────────────────────────────────────────────
// mode = 'wide'  → leyenda a tamaño natural
// mode = 'split' → dividir en N tiras (para leyendas verticales altas)
const LAYER_DOCS = [
  { key: 'eea_combined', label: 'Natura 2000 · todas (LIC+ZEC+ZEPA)',   note: 'Combinación de Hábitats + Aves · European Environment Agency. Uso comercial con atribución.', mode: 'wide' },
  { key: 'eea_habitats', label: 'Natura 2000 · LIC/ZEC (Hábitats)',     note: 'Lugares de Importancia Comunitaria + Zonas Especiales Conservación · Directiva 92/43/CEE.', mode: 'wide' },
  { key: 'eea_birds',    label: 'Natura 2000 · ZEPA (Aves)',            note: 'Zonas Especiales Protección Aves · Directiva 2009/147/CE.', mode: 'wide' },
]

// ─── GetLegendGraphic helper ──────────────────────────────────────────────
// ArcGIS MapServer WMS exige `version=1.3.0` explícito para devolver el PNG;
// si falta, responde con un XML de error.
function legendUrl(wmsBase, layerName) {
  const p = new URLSearchParams({
    service: 'WMS',
    version: '1.3.0',
    request: 'GetLegendGraphic',
    format: 'image/png',
    layer: layerName,
    sld_version: '1.1.0',
  })
  return `${wmsBase}${wmsBase.includes('?') ? '&' : '?'}${p.toString()}`
}

export default function BiodiversityMap({ latitude, longitude, addressLabel, province, height = 420, projectId }) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)

  // Capa por defecto: Natura 2000 combinado (LIC/ZEC + ZEPA) de EEA
  const defaultLayer = 'eea_combined'
  const [activeLayer, setActiveLayer] = useState(defaultLayer)

  // Snapshot de datos del entorno (Protected Planet, GBIF, GFW, ENCORE)
  const [snapshot, setSnapshot] = useState(null)
  const [fetchingSnapshot, setFetchingSnapshot] = useState(false)
  const [snapshotError, setSnapshotError] = useState(null)

  // Cargar snapshot más reciente al mount
  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('biodiversity_snapshots')
        .select('id, fetched_at, radius_km, summary, errors, wdpa_areas, gbif_species, gfw_loss, encore_deps')
        .eq('project_id', projectId)
        .order('fetched_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (!cancelled && data) setSnapshot(data)
    })()
    return () => { cancelled = true }
  }, [projectId])

  async function obtenerDatos() {
    if (!projectId) { alert('Falta projectId'); return }
    setFetchingSnapshot(true)
    setSnapshotError(null)
    try {
      const { data, error } = await supabase.functions.invoke('biodiversity-fetch', {
        body: { project_id: projectId, radius_km: 5, nace: 'I55' },
      })
      if (error) throw new Error(error.message || 'invoke failed')
      if (!data?.ok) throw new Error(data?.error || 'respuesta inesperada')
      // Recargar snapshot recién creado
      const { data: snap } = await supabase
        .from('biodiversity_snapshots')
        .select('id, fetched_at, radius_km, summary, errors, wdpa_areas, gbif_species, gfw_loss, encore_deps')
        .eq('id', data.snapshot_id)
        .maybeSingle()
      if (snap) setSnapshot(snap)
    } catch (e) {
      setSnapshotError(e?.message || String(e))
    } finally {
      setFetchingSnapshot(false)
    }
  }

  useEffect(() => {
    // Leaflet se carga global via <script> en index.html (igual que en los otros mapas)
    const L = window.L
    if (!L || !containerRef.current) return
    if (mapRef.current) return  // ya montado

    const lat = Number(latitude), lon = Number(longitude)
    if (!isFinite(lat) || !isFinite(lon)) return

    const map = L.map(containerRef.current, {
      center: [lat, lon], zoom: 12, scrollWheelZoom: true,
    })
    // Mapa base: Esri World Light Gray Canvas (free, uso comercial permitido
    // con atribución, no requiere API key). Es la alternativa estable a OSM
    // directo (prohíbe uso comercial) y a CartoDB (ahora requiere key).
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles © Esri', maxZoom: 16,
    }).addTo(map)
    // Capa de etiquetas / referencias sobre el fondo gris
    L.marker([lat, lon]).addTo(map)
      .bindPopup(`<b>${addressLabel || 'Proyecto'}</b><br>${lat.toFixed(5)}, ${lon.toFixed(5)}`)

    // Capas WMS · se añaden según el toggle
    const wmsLayers = {}
    const addWms = (key, baseUrl, layerName, extraParams = {}) => {
      wmsLayers[key] = L.tileLayer.wms(baseUrl, {
        layers: layerName,
        format: 'image/png',
        transparent: true,
        opacity: 0.55,
        ...extraParams,
      })
    }

    // EEA Natura 2000 WMS · layer 0 = Habitats, 1 = Birds, 2 = Combined
    addWms('eea_combined', WMS_EEA_NATURA, '2')
    addWms('eea_habitats', WMS_EEA_NATURA, '0')
    addWms('eea_birds',    WMS_EEA_NATURA, '1')

    // Mostrar la activa por defecto
    wmsLayers[defaultLayer]?.addTo(map)

    mapRef.current = { map, wmsLayers }
    return () => { map.remove(); mapRef.current = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude])

  // Switch de capa activa
  useEffect(() => {
    const ctx = mapRef.current
    if (!ctx) return
    const { map, wmsLayers } = ctx
    Object.entries(wmsLayers).forEach(([key, layer]) => {
      if (key === activeLayer) {
        if (!map.hasLayer(layer)) layer.addTo(map)
      } else {
        if (map.hasLayer(layer)) map.removeLayer(layer)
      }
    })
  }, [activeLayer])

  // ─── UI ────────────────────────────────────────────────────────────────
  const C = { text: '#2C2017', muted: '#7A6A5A', border: '#DDD5C8', olive: '#8D9A2E', bg: '#FFFFFF', bgAlt: '#FAF7F3' }
  const visibleLayers = LAYER_DOCS

  // Formatea un timestamp ISO como "DD/MM/YYYY HH:mm"
  const fmtDate = (ts) => {
    if (!ts) return '—'
    const d = new Date(ts)
    return `${d.getDate().toString().padStart(2,'0')}/${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
  }

  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '0.75rem', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ fontSize: '0.78rem', color: C.muted, marginBottom: '0.5rem' }}>
        Mapa interactivo · superpone capas WMS oficiales de biodiversidad sobre el emplazamiento del proyecto. Alimenta ESRS E4-5, EU Taxonomy Obj 6 (DNSH), TNFD LEAP y BREEAM USO.
      </div>

      {/* Toggle de capa activa */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.6rem' }}>
        {visibleLayers.map(l => (
          <button
            key={l.key}
            onClick={() => setActiveLayer(l.key)}
            title={l.note}
            style={{
              padding: '4px 10px', fontSize: '0.72rem', borderRadius: 3, cursor: 'pointer',
              background: activeLayer === l.key ? C.olive : 'white',
              color: activeLayer === l.key ? 'white' : C.text,
              border: `1px solid ${activeLayer === l.key ? C.olive : C.border}`,
              fontFamily: 'inherit', fontWeight: activeLayer === l.key ? 700 : 400,
            }}
          >
            {l.label}
          </button>
        ))}
      </div>

      {/* Mapa */}
      <div ref={containerRef} style={{ width: '100%', height, borderRadius: 4, overflow: 'hidden', border: `1px solid ${C.border}` }} />

      {/* ─── Panel de datos del entorno (snapshot) ─── */}
      {projectId && (
        <div style={{ marginTop: '0.6rem', padding: '0.6rem 0.8rem', background: C.bgAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.4rem' }}>
            <div style={{ fontSize: '0.82rem', color: C.text, fontWeight: 700 }}>
              Datos del entorno · APIs externas
              {snapshot && (
                <span style={{ fontSize: '0.72rem', color: C.muted, fontWeight: 400, marginLeft: '0.5rem' }}>
                  (consulta del {fmtDate(snapshot.fetched_at)} · radio {snapshot.radius_km} km)
                </span>
              )}
            </div>
            <button
              onClick={obtenerDatos}
              disabled={fetchingSnapshot}
              style={{
                padding: '5px 10px', fontSize: '0.75rem', borderRadius: 3, cursor: fetchingSnapshot ? 'wait' : 'pointer',
                background: fetchingSnapshot ? C.muted : C.olive, color: 'white', border: 'none', fontWeight: 600,
              }}
            >
              {fetchingSnapshot ? 'Consultando…' : (snapshot ? '↻ Actualizar' : 'Obtener datos')}
            </button>
          </div>

          {snapshotError && (
            <div style={{ fontSize: '0.75rem', color: '#C33', marginBottom: '0.4rem' }}>
              {snapshotError}
            </div>
          )}

          {snapshot && snapshot.summary && (() => {
            const s = snapshot.summary
            const errs = snapshot.errors || {}
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.5rem', fontSize: '0.78rem' }}>
                <div style={{ padding: '0.4rem 0.6rem', background: 'white', borderRadius: 3, border: `1px solid ${C.border}` }}>
                  <div style={{ color: C.muted, fontSize: '0.7rem' }}>Especies observadas (GBIF)</div>
                  <div style={{ fontWeight: 700, fontSize: '1.3rem', color: C.olive }}>{s.n_species_in_radius || 0}</div>
                  <div style={{ color: C.muted, fontSize: '0.68rem' }}>{s.total_occurrences || 0} ocurrencias totales</div>
                </div>
                <div style={{ padding: '0.4rem 0.6rem', background: 'white', borderRadius: 3, border: `1px solid ${C.border}` }}>
                  <div style={{ color: C.muted, fontSize: '0.7rem' }}>Áreas Natura 2000 (EEA)</div>
                  <div style={{ fontWeight: 700, fontSize: '1.3rem', color: errs.natura2000 ? '#C33' : C.olive }}>
                    {errs.natura2000 ? '—' : (s.n_protected_areas || 0)}
                  </div>
                  <div style={{ color: C.muted, fontSize: '0.68rem' }}>
                    {errs.natura2000
                      ? 'error EEA'
                      : `${s.n_protected_areas_lic_zec || 0} LIC/ZEC · ${s.n_protected_areas_zepa || 0} ZEPA`}
                  </div>
                </div>
                <div style={{ padding: '0.4rem 0.6rem', background: 'white', borderRadius: 3, border: `1px solid ${C.border}` }}>
                  <div style={{ color: C.muted, fontSize: '0.7rem' }}>Dependencias ENCORE</div>
                  <div style={{ fontWeight: 700, fontSize: '1.3rem', color: C.olive }}>{s.n_dependencies || 0}</div>
                  <div style={{ color: C.muted, fontSize: '0.68rem' }}>+ {s.n_impacts || 0} impactos</div>
                </div>
              </div>
            )
          })()}

          {snapshot && snapshot.wdpa_areas && Array.isArray(snapshot.wdpa_areas.areas) && snapshot.wdpa_areas.areas.length > 0 && (
            <details style={{ marginTop: '0.5rem', fontSize: '0.75rem' }} open>
              <summary style={{ cursor: 'pointer', color: C.olive, fontWeight: 600 }}>
                Áreas Natura 2000 en el entorno ({snapshot.wdpa_areas.areas.length})
              </summary>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.4rem', fontSize: '0.72rem' }}>
                <thead>
                  <tr style={{ background: 'white', borderBottom: `1px solid ${C.border}` }}>
                    <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted }}>Código</th>
                    <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted }}>Nombre</th>
                    <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted }}>Tipo</th>
                    <th style={{ padding: '3px 6px', textAlign: 'right', color: C.muted }}>Superficie</th>
                    <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted }}>Última act.</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.wdpa_areas.areas.map((a, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '3px 6px', color: C.text, fontFamily: 'monospace' }}>{a.sitecode}</td>
                      <td style={{ padding: '3px 6px', color: C.text }}>{a.name}</td>
                      <td style={{ padding: '3px 6px', color: C.muted, fontSize: '0.68rem' }}>{a.type}</td>
                      <td style={{ padding: '3px 6px', color: C.text, textAlign: 'right', fontSize: '0.68rem' }}>
                        {a.area_ha ? `${Math.round(a.area_ha).toLocaleString()} ha` : '—'}
                      </td>
                      <td style={{ padding: '3px 6px', color: C.muted, fontSize: '0.65rem' }}>{a.release_date || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: '0.3rem', fontSize: '0.65rem', color: C.muted, fontStyle: 'italic' }}>
                Fuente: {snapshot.wdpa_areas.source || 'EEA · Natura 2000'} · {snapshot.wdpa_areas.attribution || '© European Environment Agency'}
              </div>
            </details>
          )}

          {snapshot && snapshot.encore_deps && (
            <details style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
              <summary style={{ cursor: 'pointer', color: C.olive, fontWeight: 600 }}>
                Dependencias e impactos ecosistémicos sector hotelero (ENCORE · {(snapshot.encore_deps.dependencies || []).length} + {(snapshot.encore_deps.impacts || []).length})
              </summary>
              <div style={{ marginTop: '0.4rem' }}>
                <div style={{ fontWeight: 700, color: C.text, marginBottom: '0.2rem', fontSize: '0.72rem' }}>
                  DEPENDENCIAS (lo que el sector necesita de la naturaleza)
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
                  <thead>
                    <tr style={{ background: 'white', borderBottom: `1px solid ${C.border}` }}>
                      <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted, width: 40 }}>Nivel</th>
                      <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted }}>Servicio</th>
                      <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted }}>Contexto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(snapshot.encore_deps.dependencies || []).map((d, i) => {
                      const col = d.severity === 'VH' ? '#C33' : d.severity === 'H' ? '#C98A2E' : d.severity === 'M' ? C.olive : C.muted
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: '3px 6px', color: col, fontWeight: 700, textAlign: 'center' }}>{d.severity}</td>
                          <td style={{ padding: '3px 6px', color: C.text }}>{d.service}</td>
                          <td style={{ padding: '3px 6px', color: C.muted, fontSize: '0.65rem' }}>{d.note}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <div style={{ fontWeight: 700, color: C.text, margin: '0.6rem 0 0.2rem', fontSize: '0.72rem' }}>
                  IMPACTOS (lo que el sector causa a la naturaleza)
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
                  <thead>
                    <tr style={{ background: 'white', borderBottom: `1px solid ${C.border}` }}>
                      <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted, width: 40 }}>Nivel</th>
                      <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted }}>Impacto</th>
                      <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted }}>Contexto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(snapshot.encore_deps.impacts || []).map((d, i) => {
                      const col = d.severity === 'VH' ? '#C33' : d.severity === 'H' ? '#C98A2E' : d.severity === 'M' ? C.olive : C.muted
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: '3px 6px', color: col, fontWeight: 700, textAlign: 'center' }}>{d.severity}</td>
                          <td style={{ padding: '3px 6px', color: C.text }}>{d.service}</td>
                          <td style={{ padding: '3px 6px', color: C.muted, fontSize: '0.65rem' }}>{d.note}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: '0.3rem', fontSize: '0.65rem', color: C.muted, fontStyle: 'italic' }}>
                  Fuente: ENCORE · Natural Capital Finance Alliance · sector NACE I55 Accommodation
                </div>
              </div>
            </details>
          )}

          {snapshot && snapshot.gbif_species && snapshot.gbif_species.species_in_radius && (
            <details style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
              <summary style={{ cursor: 'pointer', color: C.olive, fontWeight: 600 }}>
                Top 20 especies observadas en el entorno ({snapshot.gbif_species.species_in_radius.length} totales)
              </summary>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.4rem', fontSize: '0.72rem' }}>
                <thead>
                  <tr style={{ background: 'white', borderBottom: `1px solid ${C.border}` }}>
                    <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted }}>Especie (nombre científico)</th>
                    <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted }}>Nombre común</th>
                    <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted }}>Reino</th>
                    <th style={{ padding: '3px 6px', textAlign: 'right', color: C.muted }}>Observaciones</th>
                    <th style={{ padding: '3px 6px', textAlign: 'left', color: C.muted }}>IUCN</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.gbif_species.species_in_radius.slice(0, 20).map((sp, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '3px 6px', fontStyle: 'italic', color: C.text }}>{sp.name}</td>
                      <td style={{ padding: '3px 6px', color: C.text, fontSize: '0.68rem' }}>{sp.vernacular || '—'}</td>
                      <td style={{ padding: '3px 6px', color: C.muted }}>{sp.kingdom || '—'}</td>
                      <td style={{ padding: '3px 6px', textAlign: 'right', color: C.text }}>{sp.count}</td>
                      <td style={{ padding: '3px 6px', color: sp.iucn === 'CR' ? '#C33' : sp.iucn === 'EN' ? '#C98A2E' : C.muted, fontWeight: ['CR','EN','VU'].includes(sp.iucn) ? 700 : 400 }}>
                        {sp.iucn && sp.iucn !== '?' ? sp.iucn : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}

      {/* Leyenda de la capa activa */}
      {(() => {
        const active = LAYER_DOCS.find(l => l.key === activeLayer)
        if (!active) return null
        const wmsBase = WMS_EEA_NATURA
        const layerName = {
          eea_combined: '2',
          eea_habitats: '0',
          eea_birds:    '1',
        }[active.key] || '0'
        return (
          <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: C.bgAlt, borderRadius: 4, border: `1px solid ${C.border}`, fontSize: '0.73rem', color: C.text }}>
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{active.label}</div>
            <div style={{ color: C.muted, marginBottom: '0.3rem' }}>{active.note}</div>
            <SplitLegend url={legendUrl(wmsBase, layerName)} splits={1} height={54} />
          </div>
        )
      })()}
    </div>
  )
}
